import { useEffect, useState } from 'react';
import { Undo2, RefreshCw, Search, AlertCircle, CheckCircle2, XCircle, Loader2, Banknote, ArrowLeft, FileQuestion } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/super-admin`;

const fmtVES = (n) => `VES ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const BANK_OPTIONS = [
  { code: '0102', name: 'Banco de Venezuela' },
  { code: '0105', name: 'Mercantil' },
  { code: '0108', name: 'Provincial' },
  { code: '0114', name: 'Bancaribe' },
  { code: '0115', name: 'Exterior' },
  { code: '0128', name: 'Banco Caroní' },
  { code: '0134', name: 'Banesco' },
  { code: '0137', name: 'Sofitasa' },
  { code: '0138', name: 'Banco Plaza' },
  { code: '0151', name: 'BFC' },
  { code: '0156', name: '100% Banco' },
  { code: '0157', name: 'DelSur' },
  { code: '0163', name: 'Banco del Tesoro' },
  { code: '0166', name: 'Banco Agrícola' },
  { code: '0168', name: 'Bancrecer' },
  { code: '0169', name: 'Mi Banco' },
  { code: '0171', name: 'Bancamiga' },
  { code: '0172', name: 'Bancamiga Banco Universal' },
  { code: '0174', name: 'Banplus' },
  { code: '0175', name: 'Bicentenario' },
  { code: '0177', name: 'BANFANB' },
  { code: '0191', name: 'Banco Nacional de Crédito' },
];

export default function ExpiredRefundsTab({ setToast }) {
  const [mode, setMode] = useState('expired'); // 'expired' | 'orphan'
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [concentrators, setConcentrators] = useState([]);
  const [selectedConcentratorId, setSelectedConcentratorId] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [destination, setDestination] = useState({
    account: '', bank_code: '0102', doc_type: 'V', doc_number: '',
    name: '', phone: '', email: '', concepto: '',
  });
  // Orphan mode state
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [orphanFilters, setOrphanFilters] = useState({
    concentrator_id: 'caura_default',
    from_date: sevenDaysAgo,
    to_date: today,
    min_amount: '',
    max_amount: '',
  });
  const [orphanData, setOrphanData] = useState(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [selectedOrphan, setSelectedOrphan] = useState(null);

  // Histórico de refunds
  const [historyData, setHistoryData] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [historyFilters, setHistoryFilters] = useState({
    status: 'all', merchant_id: 'all', from_date: '', to_date: '', search: '',
  });
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_LIMIT = 50;

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/expired-orders?limit=100`, { headers: headers() });
      setOrders(res.data.orders || []);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error cargando órdenes expiradas' });
    } finally {
      setLoading(false);
    }
  };

  const loadConcentrators = async () => {
    try {
      const res = await axios.get(`${API}/concentrators`, { headers: headers() });
      setConcentrators(res.data.concentrators || []);
    } catch (e) {
      console.warn('No se pudieron cargar concentradoras', e);
    }
  };

  useEffect(() => { loadOrders(); loadConcentrators(); loadMerchants(); }, []);

  const loadOrphans = async () => {
    setLoadingOrphans(true);
    setSelectedOrphan(null);
    try {
      const params = new URLSearchParams();
      if (orphanFilters.concentrator_id) params.set('concentrator_id', orphanFilters.concentrator_id);
      if (orphanFilters.from_date) params.set('from_date', orphanFilters.from_date);
      if (orphanFilters.to_date) params.set('to_date', orphanFilters.to_date);
      if (orphanFilters.min_amount) params.set('min_amount', orphanFilters.min_amount);
      if (orphanFilters.max_amount) params.set('max_amount', orphanFilters.max_amount);
      const res = await axios.get(`${API}/orphan-movements?${params}`, { headers: headers() });
      setOrphanData(res.data);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error buscando huérfanos' });
      setOrphanData(null);
    } finally {
      setLoadingOrphans(false);
    }
  };

  const loadMerchants = async () => {
    try {
      const res = await axios.get(`${API}/merchants`, { headers: headers() });
      setMerchants(res.data || []);
    } catch (e) {
      console.warn('No se pudieron cargar comercios', e);
    }
  };

  const loadHistory = async (page = 0) => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams();
      if (historyFilters.status && historyFilters.status !== 'all') params.set('status', historyFilters.status);
      if (historyFilters.merchant_id && historyFilters.merchant_id !== 'all') params.set('merchant_id', historyFilters.merchant_id);
      if (historyFilters.from_date) params.set('from_date', historyFilters.from_date);
      if (historyFilters.to_date) params.set('to_date', historyFilters.to_date);
      if (historyFilters.search.trim()) params.set('search', historyFilters.search.trim());
      params.set('limit', HISTORY_LIMIT);
      params.set('offset', page * HISTORY_LIMIT);
      const res = await axios.get(`${API}/refunds?${params}`, { headers: headers() });
      setHistoryData(res.data);
      setHistoryPage(page);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error cargando histórico' });
      setHistoryData(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const startOrphanRefund = (mov) => {
    setSelectedOrphan(mov);
    setDestination({
      account: '', bank_code: '0102', doc_type: 'V', doc_number: '',
      name: '', phone: '', email: '',
      concepto: `Devolución depósito ref ${mov.referencia}`,
    });
  };

  const executeOrphanRefund = async () => {
    if (!selectedOrphan) return;
    if (!destination.account || destination.account.length !== 20) {
      setToast?.({ type: 'error', message: 'Cuenta destino debe tener 20 dígitos' });
      return;
    }
    if (!destination.doc_number || !destination.name) {
      setToast?.({ type: 'error', message: 'Cédula/RIF y nombre del beneficiario son obligatorios' });
      return;
    }
    if (!window.confirm(
      `¿Confirmas el refund HUÉRFANO?\n\n` +
      `Monto: ${fmtVES(selectedOrphan.monto)}\n` +
      `Desde: ${orphanData?.concentrator?.label}\n` +
      `Destino: ${destination.doc_type}-${destination.doc_number} / cuenta ...${destination.account.slice(-4)}\n` +
      `Movimiento: ${selectedOrphan.fecha} ref ${selectedOrphan.referencia}\n\n` +
      `Esta acción NO se puede deshacer.`,
    )) return;
    setExecuting(true);
    try {
      const body = {
        concentrator_id: orphanFilters.concentrator_id,
        movement: {
          fecha: selectedOrphan.fecha,
          referencia: selectedOrphan.referencia,
          monto: selectedOrphan.monto,
          descripcion: selectedOrphan.descripcion,
        },
        destination: {
          account: destination.account.trim(),
          bank_code: destination.bank_code,
          doc_type: destination.doc_type,
          doc_number: destination.doc_number.trim(),
          name: destination.name.trim(),
          phone: destination.phone.trim() || undefined,
          email: destination.email.trim() || undefined,
        },
        concepto: destination.concepto.trim() || undefined,
      };
      const res = await axios.post(`${API}/orphan-refund`, body, { headers: headers() });
      const vstat = res.data.verification_status;
      if (vstat === 'verified') {
        setToast?.({ type: 'success', message: `✅ Refund confirmado en banco. Ref: ${res.data.bank_reference || '—'}` });
      } else {
        // pending_verification — banco respondió 0000 pero aún no se ve el débito.
        setToast?.({
          type: 'success',
          message: `⏳ Refund enviado (ref ${res.data.bank_reference || '—'}). Verificando settlement bancario en background — puede tardar hasta 2h. Se auto-falla si no se refleja.`,
        });
      }
      setSelectedOrphan(null);
      await loadOrphans();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.error) ? detail.error : (detail || 'Error ejecutando refund');
      setToast?.({ type: 'error', message: msg });
    } finally {
      setExecuting(false);
    }
  };

  const openOrder = async (order, concentratorIdOverride = null) => {
    setSelectedOrder(order);
    setCandidates(null);
    setSelectedMovement(null);
    setDestination({
      account: '', bank_code: '0102', doc_type: 'V', doc_number: '',
      name: order.payer_name || '', phone: '', email: order.payer_email || '',
      concepto: `Devolución orden ${order.order_code}`,
    });
    // Por defecto: NO seleccionar concentrator → backend usa el del proyecto.
    // El admin puede cambiar y re-buscar.
    const concId = concentratorIdOverride !== null ? concentratorIdOverride : selectedConcentratorId;
    await searchCandidates(order.order_id, concId);
  };

  const searchCandidates = async (orderId, concentratorId) => {
    setLoadingCandidates(true);
    setSelectedMovement(null);
    try {
      const params = new URLSearchParams({ window_days: '2' });
      if (concentratorId) params.set('concentrator_id', concentratorId);
      const res = await axios.get(
        `${API}/orders/${orderId}/candidate-movements?${params.toString()}`,
        { headers: headers() },
      );
      setCandidates(res.data);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error buscando movimientos en el banco' });
      setCandidates({ ok: false, candidates: [] });
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleConcentratorChange = (concId) => {
    setSelectedConcentratorId(concId);
    if (selectedOrder) {
      searchCandidates(selectedOrder.order_id, concId);
    }
  };

  const executeRefund = async () => {
    if (!selectedMovement) {
      setToast?.({ type: 'error', message: 'Selecciona primero el movimiento bancario que corresponde a esta orden' });
      return;
    }
    if (!destination.account || destination.account.length !== 20) {
      setToast?.({ type: 'error', message: 'Cuenta destino debe tener 20 dígitos' });
      return;
    }
    if (!destination.doc_number || !destination.name) {
      setToast?.({ type: 'error', message: 'Cédula/RIF y nombre del beneficiario son obligatorios' });
      return;
    }
    if (!window.confirm(
      `¿Confirmas el refund?\n\n` +
      `Monto: ${fmtVES(selectedMovement.monto)}\n` +
      `Destino: ${destination.doc_type}-${destination.doc_number} / cuenta ...${destination.account.slice(-4)}\n` +
      `Movimiento: ${selectedMovement.fecha} ref ${selectedMovement.referencia}\n\n` +
      `Esta acción NO se puede deshacer.`,
    )) return;

    setExecuting(true);
    try {
      const body = {
        concentrator_id: candidates?.concentrator?.concentrator_id || selectedConcentratorId || undefined,
        movement: {
          fecha: selectedMovement.fecha,
          referencia: selectedMovement.referencia,
          monto: selectedMovement.monto,
          descripcion: selectedMovement.descripcion,
        },
        destination: {
          account: destination.account.trim(),
          bank_code: destination.bank_code,
          doc_type: destination.doc_type,
          doc_number: destination.doc_number.trim(),
          name: destination.name.trim(),
          phone: destination.phone.trim() || undefined,
          email: destination.email.trim() || undefined,
        },
        concepto: destination.concepto.trim() || undefined,
      };
      const res = await axios.post(
        `${API}/orders/${selectedOrder.order_id}/refund-expired`,
        body, { headers: headers() },
      );
      const vstat = res.data.verification_status;
      if (vstat === 'verified') {
        setToast?.({ type: 'success', message: `✅ Refund confirmado en banco. Ref: ${res.data.bank_reference || '—'}` });
      } else {
        setToast?.({
          type: 'success',
          message: `⏳ Refund enviado (ref ${res.data.bank_reference || '—'}). Verificando settlement bancario en background — puede tardar hasta 2h. Se auto-falla si no se refleja.`,
        });
      }
      setSelectedOrder(null);
      await loadOrders();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.error) ? detail.error : (detail || 'Error ejecutando refund');
      setToast?.({ type: 'error', message: msg });
    } finally {
      setExecuting(false);
    }
  };

  // ── Orphan view renderer ─────────────────────────────────────
  const renderOrphanView = () => (
    <div data-testid="orphan-view">
      {/* Filtros */}
      <div className="bg-white border rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Concentradora</label>
            <select
              value={orphanFilters.concentrator_id}
              onChange={(e) => setOrphanFilters((f) => ({ ...f, concentrator_id: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              data-testid="orphan-concentrator-select"
            >
              {concentrators.map((c) => (
                <option key={c.concentrator_id} value={c.concentrator_id}>
                  {c.label} · …{c.cuenta_operativa.slice(-4)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Desde</label>
            <input type="date" value={orphanFilters.from_date} onChange={(e) => setOrphanFilters((f) => ({ ...f, from_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Hasta</label>
            <input type="date" value={orphanFilters.to_date} onChange={(e) => setOrphanFilters((f) => ({ ...f, to_date: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <button
              onClick={loadOrphans}
              disabled={loadingOrphans}
              className="w-full px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="search-orphans"
            >
              {loadingOrphans ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Monto mínimo (opcional)</label>
            <input type="number" step="0.01" value={orphanFilters.min_amount} onChange={(e) => setOrphanFilters((f) => ({ ...f, min_amount: e.target.value }))} placeholder="0" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Monto máximo (opcional)</label>
            <input type="number" step="0.01" value={orphanFilters.max_amount} onChange={(e) => setOrphanFilters((f) => ({ ...f, max_amount: e.target.value }))} placeholder="∞" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
      </div>

      {/* Resultados */}
      {!orphanData ? (
        <div className="text-center py-10 text-gray-500 text-sm bg-gray-50 rounded-xl border border-dashed">
          <FileQuestion className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          Selecciona la concentradora y rango de fechas, luego "Buscar".
        </div>
      ) : !orphanData.ok ? (
        <div className="text-center py-10 text-red-600 text-sm">
          <XCircle className="w-10 h-10 mx-auto mb-2" />
          {orphanData.error || 'Error consultando movimientos'}
        </div>
      ) : (
        <>
          <div className="mb-3 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
            <span><strong>Concentradora:</strong> {orphanData.concentrator.label} ({orphanData.concentrator.cuenta_operativa})</span>
            <span><strong>Inspeccionados:</strong> {orphanData.movements_inspected}</span>
            <span><strong>Créditos:</strong> {orphanData.credits_count}</span>
            <span><strong>Huérfanos:</strong> <span className="text-rose-700 font-bold">{orphanData.orphans_count}</span></span>
          </div>
          {orphanData.orphans.length === 0 ? (
            <div className="text-center py-10 bg-green-50 rounded-xl border border-green-200">
              <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-green-800 font-semibold">No hay depósitos huérfanos en esta ventana</p>
              <p className="text-xs text-gray-600 mt-1">Todos los créditos están linkeados a una orden paid/refunded o ya fueron usados en un refund.</p>
            </div>
          ) : (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Fecha/Hora</th>
                    <th className="px-4 py-3 text-left">Referencia</th>
                    <th className="px-4 py-3 text-right">Monto</th>
                    <th className="px-4 py-3 text-left">Descripción</th>
                    <th className="px-4 py-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {orphanData.orphans.map((m) => (
                    <tr key={m.mov_signature} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs">{m.fecha} {m.hora || ''}</td>
                      <td className="px-4 py-3 font-mono text-xs">{m.referencia}</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{fmtVES(m.monto)}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 truncate max-w-xs">{m.descripcion}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => startOrphanRefund(m)}
                          className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-semibold inline-flex items-center gap-1"
                          data-testid={`refund-orphan-${m.referencia}`}
                        >
                          <Undo2 className="w-3.5 h-3.5" /> Refund
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Modal de captura del beneficiario para refund huérfano */}
      {selectedOrphan && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrphan(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="orphan-refund-modal">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Refund de depósito huérfano</h3>
                <p className="text-xs text-gray-500 font-mono mt-1">
                  {fmtVES(selectedOrphan.monto)} · {selectedOrphan.fecha} · ref {selectedOrphan.referencia}
                </p>
              </div>
              <button onClick={() => setSelectedOrphan(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Este refund NO está asociado a ninguna orden. Quedará registrado como <strong>orphan</strong> en el sistema.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo doc</label>
                  <select value={destination.doc_type} onChange={(e) => setDestination((d) => ({ ...d, doc_type: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="V">V</option><option value="E">E</option>
                    <option value="J">J</option><option value="G">G</option><option value="P">P</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Cédula/RIF</label>
                  <input type="text" value={destination.doc_number} onChange={(e) => setDestination((d) => ({ ...d, doc_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="orphan-doc-number" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nombre</label>
                <input type="text" value={destination.name} onChange={(e) => setDestination((d) => ({ ...d, name: e.target.value.toUpperCase().slice(0, 80) }))} maxLength={80} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="orphan-name" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Cuenta destino (20 dígitos)</label>
                  <input type="text" value={destination.account} onChange={(e) => setDestination((d) => ({ ...d, account: e.target.value.replace(/\D/g, '').slice(0, 20) }))} maxLength={20} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="orphan-account" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Banco</label>
                  <select value={destination.bank_code} onChange={(e) => setDestination((d) => ({ ...d, bank_code: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                    {BANK_OPTIONS.map((b) => (
                      <option key={b.code} value={b.code}>{b.code} — {b.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Teléfono (opcional)</label>
                  <input type="text" value={destination.phone} onChange={(e) => setDestination((d) => ({ ...d, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email (opcional)</label>
                  <input type="email" value={destination.email} onChange={(e) => setDestination((d) => ({ ...d, email: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Concepto</label>
                <input type="text" value={destination.concepto} onChange={(e) => setDestination((d) => ({ ...d, concepto: e.target.value.slice(0, 80) }))} maxLength={80} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button onClick={() => setSelectedOrphan(null)} disabled={executing} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancelar</button>
                <button onClick={executeOrphanRefund} disabled={executing} className="px-5 py-2 text-sm bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2" data-testid="execute-orphan-refund">
                  {executing ? <><Loader2 className="w-4 h-4 animate-spin" /> Ejecutando…</> : <><Undo2 className="w-4 h-4" /> Ejecutar refund</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Histórico de refunds ────────────────────────────────────
  const RefundStatusPill = ({ status, verification }) => {
    const map = {
      success: { bg: 'bg-green-100', text: 'text-green-800', label: 'Exitoso' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Fallido' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
    };
    const s = map[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status || '—' };
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
        {verification === 'pending_verification' && (
          <span className="text-[10px] text-amber-600 font-medium">verificando…</span>
        )}
        {verification === 'verified' && status === 'success' && (
          <span className="text-[10px] text-green-600 font-medium">✓ verificado</span>
        )}
      </div>
    );
  };

  const renderHistoryView = () => {
    const totals = historyData?.totals || {};
    const refunds = historyData?.refunds || [];
    const total = historyData?.count || 0;
    const totalPages = Math.ceil(total / HISTORY_LIMIT);
    return (
      <div data-testid="refunds-history-view">
        {/* Filtros */}
        <div className="bg-gray-50 border rounded-xl p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Estado</label>
              <select
                value={historyFilters.status}
                onChange={(e) => setHistoryFilters({ ...historyFilters, status: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white"
                data-testid="history-filter-status"
              >
                <option value="all">Todos</option>
                <option value="success">Exitosos</option>
                <option value="failed">Fallidos</option>
                <option value="pending">Pendientes</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Comercio</label>
              <select
                value={historyFilters.merchant_id}
                onChange={(e) => setHistoryFilters({ ...historyFilters, merchant_id: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white"
                data-testid="history-filter-merchant"
              >
                <option value="all">Todos</option>
                {merchants.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.company_name || m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Desde</label>
              <input type="date" value={historyFilters.from_date}
                onChange={(e) => setHistoryFilters({ ...historyFilters, from_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white" data-testid="history-filter-from" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Hasta</label>
              <input type="date" value={historyFilters.to_date}
                onChange={(e) => setHistoryFilters({ ...historyFilters, to_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white" data-testid="history-filter-to" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Buscar (ref / orden)</label>
              <input type="text" placeholder="Ref bancaria u order…" value={historyFilters.search}
                onChange={(e) => setHistoryFilters({ ...historyFilters, search: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') loadHistory(0); }}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-white" data-testid="history-filter-search" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => loadHistory(0)}
              className="px-4 py-2 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-semibold inline-flex items-center gap-2"
              data-testid="history-apply-filters">
              <Search className="w-4 h-4" /> Aplicar filtros
            </button>
            <button onClick={() => { setHistoryFilters({ status: 'all', merchant_id: 'all', from_date: '', to_date: '', search: '' }); setTimeout(() => loadHistory(0), 0); }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              Limpiar
            </button>
          </div>
        </div>

        {/* Totales */}
        {historyData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white border rounded-xl p-3" data-testid="history-total-count">
              <div className="text-xs text-gray-500">Total refunds</div>
              <div className="text-xl font-bold" style={{ color: '#051C2C' }}>{total}</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <div className="text-xs text-gray-500">Exitosos ({totals.count_success || 0})</div>
              <div className="text-lg font-bold text-green-700">{fmtVES(totals.success)}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="text-xs text-gray-500">Fallidos ({totals.count_failed || 0})</div>
              <div className="text-lg font-bold text-red-700">{fmtVES(totals.failed)}</div>
            </div>
          </div>
        )}

        {/* Tabla */}
        {loadingHistory ? (
          <div className="text-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Cargando histórico…</div>
        ) : refunds.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FileQuestion className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold">No hay refunds con estos filtros</p>
          </div>
        ) : (
          <div className="bg-white border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-left">Comercio / Orden</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-right">Monto</th>
                  <th className="px-4 py-3 text-left">Destino</th>
                  <th className="px-4 py-3 text-left">Ref bancaria</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((r) => (
                  <tr key={r.refund_id || `${r.bank_reference}-${r.created_at}`} className="border-b hover:bg-gray-50" data-testid={`history-row-${r.refund_id || r.bank_reference}`}>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      {r.merchant_name ? (
                        <>
                          <div className="text-xs font-semibold">{r.merchant_name}</div>
                          <div className="text-xs text-gray-500 font-mono">{r.order_code || (r.order_id ? `${r.order_id.slice(0, 8)}…` : '—')}</div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">Sin orden (huérfano)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${r.refund_kind === 'orphan' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {r.refund_kind === 'orphan' ? 'Huérfano' : 'Por orden'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{fmtVES(r.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-medium">{r.destination?.name || '—'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.destination?.account ? `…${String(r.destination.account).slice(-4)}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-600">
                      {r.bank_reference || '—'}
                      {r.status === 'failed' && r.error_message && (
                        <div className="text-[10px] text-red-500 mt-0.5 max-w-[160px] truncate" title={r.error_message}>{r.error_message}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center"><RefundStatusPill status={r.status} verification={r.verification_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Paginación */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm">
                <span className="text-gray-500">Página {historyPage + 1} de {totalPages}</span>
                <div className="flex gap-2">
                  <button disabled={historyPage === 0} onClick={() => loadHistory(historyPage - 1)}
                    className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-white" data-testid="history-prev">Anterior</button>
                  <button disabled={historyPage + 1 >= totalPages} onClick={() => loadHistory(historyPage + 1)}
                    className="px-3 py-1.5 border rounded-lg disabled:opacity-40 hover:bg-white" data-testid="history-next">Siguiente</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };


  // ── List view ───────────────────────────────────────────────
  if (!selectedOrder) {
    return (
      <div data-testid="expired-refunds-tab">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#051C2C' }}>
              <Undo2 className="w-7 h-7 text-rose-600" />
              Refunds (Expirados + Huérfanos)
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Devuelve fondos a clientes cuando la orden expiró o cuando el cliente depositó un monto incorrecto.
              Anti doble-refund garantizado por hash único del movimiento bancario.
            </p>
          </div>
          <button
            onClick={() => mode === 'expired' ? loadOrders() : mode === 'orphan' ? loadOrphans() : loadHistory(historyPage)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
            data-testid="refresh-refunds"
          >
            <RefreshCw className="w-4 h-4" /> Refrescar
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex gap-1 border-b mb-5">
          <button
            onClick={() => setMode('expired')}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${mode === 'expired' ? 'border-rose-600 text-rose-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            data-testid="mode-expired"
          >
            <Undo2 className="w-4 h-4 inline mr-1" /> Por orden expirada
          </button>
          <button
            onClick={() => setMode('orphan')}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${mode === 'orphan' ? 'border-rose-600 text-rose-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            data-testid="mode-orphan"
          >
            <FileQuestion className="w-4 h-4 inline mr-1" /> Depósitos huérfanos
          </button>
          <button
            onClick={() => { setMode('history'); if (!historyData) loadHistory(0); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${mode === 'history' ? 'border-rose-600 text-rose-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            data-testid="mode-history"
          >
            <Banknote className="w-4 h-4 inline mr-1" /> Histórico
          </button>
        </div>

        {mode === 'expired' && (
          loading ? (
            <div className="text-center py-12 text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Cargando órdenes…</div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600 font-semibold">No hay órdenes expiradas pendientes</p>
            </div>
          ) : (
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Orden</th>
                    <th className="px-4 py-3 text-right">Monto</th>
                    <th className="px-4 py-3 text-left">Pagador</th>
                    <th className="px-4 py-3 text-left">Creada</th>
                    <th className="px-4 py-3 text-center">Refund existente</th>
                    <th className="px-4 py-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-b hover:bg-gray-50" data-testid={`expired-order-${o.order_code}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono font-semibold">{o.order_code}</div>
                        <div className="text-xs text-gray-500">{o.order_id.slice(0, 8)}…</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{fmtVES(o.amount)}</td>
                      <td className="px-4 py-3">
                        <div className="text-xs font-semibold">{o.payer_name || '—'}</div>
                        <div className="text-xs text-gray-500">{o.payer_email || ''}</div>
                        <div className="text-xs text-gray-500 font-mono">{o.payer_document || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-3 text-center">
                        {o.existing_refund ? (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 font-semibold">
                            {o.existing_refund.status}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {o.existing_refund ? (
                          <span className="text-xs text-gray-400">Bloqueado</span>
                        ) : (
                          <button
                            onClick={() => openOrder(o)}
                            className="px-3 py-1.5 text-xs bg-rose-600 text-white rounded-lg hover:bg-rose-700 font-semibold inline-flex items-center gap-1"
                            data-testid={`open-refund-${o.order_code}`}
                          >
                            <Search className="w-3.5 h-3.5" /> Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {mode === 'orphan' && renderOrphanView()}

        {mode === 'history' && renderHistoryView()}
      </div>
    );
  }

  // ── Detail view ─────────────────────────────────────────────
  return (
    <div data-testid="expired-refund-detail">
      <button onClick={() => { setSelectedOrder(null); setCandidates(null); setSelectedMovement(null); }} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Volver al listado
      </button>

      <div className="bg-gradient-to-br from-rose-50 to-orange-50 border-2 border-rose-200 rounded-xl p-5 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Undo2 className="w-7 h-7 text-rose-600" />
          Refund manual · Orden {selectedOrder.order_code}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 mt-3 text-xs text-gray-700">
          <div><span className="text-gray-500">Monto:</span> <span className="font-bold text-gray-900">{fmtVES(selectedOrder.amount)}</span></div>
          <div><span className="text-gray-500">Pagador:</span> <span className="font-semibold">{selectedOrder.payer_name || '—'}</span></div>
          <div><span className="text-gray-500">Doc:</span> <span className="font-mono">{selectedOrder.payer_document || '—'}</span></div>
          <div><span className="text-gray-500">Creada:</span> <span>{fmtDate(selectedOrder.created_at)}</span></div>
        </div>
      </div>

      {/* Paso 1: Selector de concentradora + búsqueda de movimientos */}
      <div className="bg-white border rounded-xl mb-6">
        <div className="px-5 py-3 border-b bg-gray-50">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Banknote className="w-5 h-5 text-blue-600" />
            Paso 1 · Concentradora donde entró el depósito
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Selecciona la cuenta concentradora donde el cliente depositó. La búsqueda y el CCE saldrán desde esta cuenta.</p>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {concentrators.map((c) => {
              const isActive = (selectedConcentratorId || candidates?.concentrator?.concentrator_id || (concentrators[0]?.concentrator_id)) === c.concentrator_id;
              return (
                <button
                  key={c.concentrator_id}
                  onClick={() => handleConcentratorChange(c.concentrator_id)}
                  className={`text-left p-3 border-2 rounded-lg transition ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  data-testid={`select-concentrator-${c.concentrator_id}`}
                >
                  <div className="font-semibold text-sm">{c.label}</div>
                  <div className="text-xs text-gray-500 mt-1 font-mono">
                    {c.cuenta_operativa} · {c.titular_name || '—'}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    RIF {c.rif} {c.owns ? '· Propia' : '· Default Caura'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Paso 2: Movimientos candidatos */}
      <div className="bg-white border rounded-xl mb-6">
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              Paso 2 · Selecciona el movimiento bancario
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Movimientos CR (créditos) con monto coincidente, ventana ±2 días
              {candidates?.concentrator && (
                <> · Cuenta: <span className="font-mono">{candidates.concentrator.cuenta_operativa}</span></>
              )}
            </p>
          </div>
          <button onClick={() => searchCandidates(selectedOrder.order_id, selectedConcentratorId)} disabled={loadingCandidates} className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-100 flex items-center gap-1 disabled:opacity-50" data-testid="reload-candidates">
            <RefreshCw className={`w-3 h-3 ${loadingCandidates ? 'animate-spin' : ''}`} /> Recargar
          </button>
        </div>

        {loadingCandidates ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Consultando Banco Plaza…</div>
        ) : !candidates || !candidates.ok ? (
          <div className="p-6 text-center text-red-600 text-sm">
            <XCircle className="w-8 h-8 mx-auto mb-2" />
            No se pudieron consultar los movimientos. Reintenta o verifica conectividad.
          </div>
        ) : candidates.candidates.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            No se encontró ningún depósito en la cuenta concentradora {candidates.concentrator?.cuenta_operativa || candidates.caura_account} con monto {fmtVES(selectedOrder.amount)} en la ventana {candidates.search_window.from} → {candidates.search_window.to}.
            <div className="text-xs text-gray-400 mt-2">Movimientos inspeccionados: {candidates.movements_inspected}</div>
            <div className="text-xs text-amber-700 mt-2">Si el cliente depositó en otra concentradora, cambia la selección en el Paso 1 y vuelve a buscar.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-center">Sel.</th>
                  <th className="px-4 py-2 text-left">Fecha</th>
                  <th className="px-4 py-2 text-left">Referencia</th>
                  <th className="px-4 py-2 text-right">Monto</th>
                  <th className="px-4 py-2 text-left">Descripción</th>
                  <th className="px-4 py-2 text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {candidates.candidates.map((m) => (
                  <tr key={m.mov_signature} className={`border-b ${m.already_refunded ? 'bg-gray-100 text-gray-400' : 'hover:bg-blue-50'} ${selectedMovement?.mov_signature === m.mov_signature ? 'bg-blue-100' : ''}`}>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="radio"
                        name="movement"
                        disabled={m.already_refunded}
                        checked={selectedMovement?.mov_signature === m.mov_signature}
                        onChange={() => setSelectedMovement(m)}
                        data-testid={`select-mov-${m.referencia}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-xs">{m.fecha} {m.hora || ''}</td>
                    <td className="px-4 py-2 font-mono text-xs">{m.referencia}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtVES(m.monto)}</td>
                    <td className="px-4 py-2 text-xs text-gray-600 truncate max-w-xs">{m.descripcion}</td>
                    <td className="px-4 py-2 text-center">
                      {m.already_refunded ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-semibold">YA usado</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-semibold">Disponible</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paso 2: Datos del beneficiario */}
      {selectedMovement && (
        <div className="bg-white border rounded-xl mb-6">
          <div className="px-5 py-3 border-b bg-gray-50">
            <h3 className="font-bold text-gray-800">Paso 3 · Datos del beneficiario (a quién devuelves)</h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Tipo doc</label>
                <select value={destination.doc_type} onChange={(e) => setDestination((d) => ({ ...d, doc_type: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="ref-doc-type">
                  <option value="V">V (cédula)</option>
                  <option value="E">E (extranjero)</option>
                  <option value="J">J (jurídico)</option>
                  <option value="G">G (gubernamental)</option>
                  <option value="P">P (pasaporte)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Número doc (cédula/RIF sin guiones)</label>
                <input type="text" value={destination.doc_number} onChange={(e) => setDestination((d) => ({ ...d, doc_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} placeholder="12345678" className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="ref-doc-number" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Nombre del beneficiario</label>
              <input type="text" value={destination.name} onChange={(e) => setDestination((d) => ({ ...d, name: e.target.value.toUpperCase().slice(0, 80) }))} placeholder="JUAN PEREZ" maxLength={80} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="ref-name" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Cuenta destino (20 dígitos)</label>
                <input type="text" value={destination.account} onChange={(e) => setDestination((d) => ({ ...d, account: e.target.value.replace(/\D/g, '').slice(0, 20) }))} placeholder="01020000..." maxLength={20} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="ref-account" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Banco destino</label>
                <select value={destination.bank_code} onChange={(e) => setDestination((d) => ({ ...d, bank_code: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="ref-bank-code">
                  {BANK_OPTIONS.map((b) => (
                    <option key={b.code} value={b.code}>{b.code} — {b.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Teléfono (opcional)</label>
                <input type="text" value={destination.phone} onChange={(e) => setDestination((d) => ({ ...d, phone: e.target.value }))} placeholder="04141234567" className="w-full px-3 py-2 border rounded-lg text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email (opcional)</label>
                <input type="email" value={destination.email} onChange={(e) => setDestination((d) => ({ ...d, email: e.target.value }))} placeholder="cliente@email.com" className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Concepto del CCE (máx 80 chars)</label>
              <input type="text" value={destination.concepto} onChange={(e) => setDestination((d) => ({ ...d, concepto: e.target.value.slice(0, 80) }))} maxLength={80} className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
          </div>
        </div>
      )}

      {/* Paso 3: Confirmar y ejecutar */}
      {selectedMovement && (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-5 flex items-start gap-4">
          <AlertCircle className="w-8 h-8 text-rose-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h4 className="font-bold text-rose-900">Paso 4 · Ejecutar refund</h4>
            <p className="text-sm text-rose-800 mt-1">
              Se transferirá <strong>{fmtVES(selectedMovement.monto)}</strong> desde
              <strong> {candidates?.concentrator?.label || 'la cuenta concentradora'} </strong>
              (<span className="font-mono">{candidates?.concentrator?.cuenta_operativa}</span>) a la cuenta destino vía CCE.
              El movimiento bancario quedará marcado como usado y NO podrá reutilizarse en otro refund.
            </p>
            <button
              onClick={executeRefund}
              disabled={executing || !destination.account || !destination.doc_number || !destination.name}
              className="mt-3 px-5 py-2.5 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="execute-refund-btn"
            >
              {executing ? <><Loader2 className="w-4 h-4 animate-spin" /> Ejecutando…</> : <><Undo2 className="w-4 h-4" /> Ejecutar refund</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
